import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  PRODUCTS_50, 
  PRODUCT_CATEGORIES, 
  WebProduct 
} from '../data/productWebsiteData';
import { collection, query, onSnapshot, doc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/AuthContext';
import { 
  ShoppingBag, 
  CheckCircle, 
  ShieldCheck, 
  Activity, 
  Sparkles, 
  ExternalLink, 
  Percent, 
  Landmark, 
  Award, 
  Layers, 
  Globe, 
  Cpu, 
  Check, 
  Plus, 
  Trash2, 
  ChevronRight, 
  ChevronDown,
  HelpCircle, 
  X,
  Laptop,
  Maximize2,
  Heart,
  Gamepad,
  Monitor,
  Tv,
  Mouse,
  Wifi,
  Star,
  Truck, AlertTriangle,
  RotateCcw,
  Headphones,
  Gift,
  ShieldAlert,
  Search,
  Sliders,
  Filter,
  RefreshCw,
  Clock,
  Phone,
  ArrowRight,
  Database,
  Lock,
  Compass,
  Zap,
  Info,
  Coins
} from 'lucide-react';

export function NexoraXMarketplaceView() {
  // ----------------------------------------------------
  // Core Operational States & Dynamic Products
  // ----------------------------------------------------
  const { user } = useAuth();

  // ----------------------------------------------------
  // Core Operational States & Dynamic Products
  // ----------------------------------------------------
  const [products, setProducts] = useState<WebProduct[]>(() => {
    try {
      const stored = localStorage.getItem('nexorax_marketplace_products');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {}
    return PRODUCTS_50;
  });

  const [selectedUserProfile, setSelectedUserProfile] = useState<'individual' | 'business' | 'government'>('individual');
  const [selectedCurrency, setSelectedCurrency] = useState<'GBP' | 'USD' | 'EUR'>('GBP');

  const currencyInfo = {
    GBP: { symbol: '£', rate: 1.0, label: 'GBP (£)' },
    USD: { symbol: '$', rate: 1.25, label: 'USD ($)' },
    EUR: { symbol: '€', rate: 1.18, label: 'EUR (€)' },
  };

  const formatNexoraXCurrency = (val: number, decimals: number = 2) => {
    const localeMap = {
      GBP: 'en-GB',
      USD: 'en-US',
      EUR: 'de-DE'
    };
    return new Intl.NumberFormat(localeMap[selectedCurrency] || 'en-GB', {
      style: 'currency',
      currency: selectedCurrency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(val);
  };

  const [selectedProduct, setSelectedProduct] = useState<WebProduct>(() => {
    try {
      const stored = localStorage.getItem('nexorax_marketplace_products');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.length > 0) return parsed[20] || parsed[0];
      }
    } catch (e) {}
    return PRODUCTS_50[20];
  });

  // Admin and Inline Edit states
  const [isAdminMode, setIsAdminMode] = useState<boolean>(false);
  const [editName, setEditName] = useState('');
  const [editCodename, setEditCodename] = useState('');
  const [editCategory, setEditCategory] = useState<'Infrastructure' | 'Communications' | 'Neural' | 'Workspace' | 'Security'>('Infrastructure');
  const [editSubcategory, setEditSubcategory] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPrice, setEditPrice] = useState<number>(0);
  const [editBadge, setEditBadge] = useState('');
  const [editGlowColor, setEditGlowColor] = useState('cyan');
  
  // 3D configs
  const [editGeometryType, setEditGeometryType] = useState<'circle' | 'cube' | 'prism' | 'torus' | 'sphere'>('circle');
  const [editGlowIntensity, setEditGlowIntensity] = useState<number>(15);
  const [editStrokeWidth, setEditStrokeWidth] = useState<number>(1);
  const [editPulseRate, setEditPulseRate] = useState<'none' | 'slow' | 'fast'>('slow');

  // Specs
  const [editSpecKey1, setEditSpecKey1] = useState('Throughput');
  const [editSpecVal1, setEditSpecVal1] = useState('NexoraX 0.8s');
  const [editSpecKey2, setEditSpecKey2] = useState('Encryption');
  const [editSpecVal2, setEditSpecVal2] = useState('SHA-512 Quantum');
  const [editSpecKey3, setEditSpecKey3] = useState('Vault Sync');
  const [editSpecVal3, setEditSpecVal3] = useState('CloudSync-02 Active');

  // Features
  const [editFeatures, setEditFeatures] = useState<string[]>([]);
  const [newFeatureText, setNewFeatureText] = useState('');
  const [isSavingProduct, setIsSavingProduct] = useState(false);

  // Firestore products subscription
  useEffect(() => {
    const q = query(collection(db, 'products'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        // Automatically seed Firestore with initial 50 products if remote db is empty
        PRODUCTS_50.forEach(async (p) => {
          try {
            await setDoc(doc(db, 'products', p.id), p);
          } catch (e) {
            console.error("Firestore base seeding error:", p.id, e);
          }
        });
      } else {
        const dbProducts: WebProduct[] = [];
        snapshot.forEach((doc) => {
          dbProducts.push(doc.data() as WebProduct);
        });
        dbProducts.sort((a, b) => {
          const numA = parseInt(a.id.replace(/\D/g, '')) || 0;
          const numB = parseInt(b.id.replace(/\D/g, '')) || 0;
          return numA - numB;
        });
        setProducts(dbProducts);
        
        // Sync to offline Cache & emit event
        localStorage.setItem('nexorax_marketplace_products', JSON.stringify(dbProducts));
        window.dispatchEvent(new Event('nexorax-products-updated'));
      }
    }, (error) => {
      console.error("Firestore products snapshot sync failed, keeping offline cache:", error);
    });

    return () => unsubscribe();
  }, []);

  // Sync edits when selected product is loaded
  useEffect(() => {
    if (selectedProduct) {
      setEditName(selectedProduct.name || '');
      setEditCodename(selectedProduct.codename || '');
      setEditCategory(selectedProduct.category || 'Infrastructure');
      setEditSubcategory(selectedProduct.subcategory || '');
      setEditDescription(selectedProduct.description || '');
      setEditPrice(selectedProduct.price || 0);
      setEditBadge(selectedProduct.badge || '');
      setEditGlowColor(selectedProduct.glowColor || 'cyan');
      
      // 3D configs using cast
      setEditGeometryType((selectedProduct as any).geometryType || 'circle');
      setEditGlowIntensity((selectedProduct as any).glowIntensity || 15);
      setEditStrokeWidth((selectedProduct as any).strokeWidth || 1);
      setEditPulseRate((selectedProduct as any).pulseRate || 'slow');

      // Spec keys and features
      const specKeys = Object.keys(selectedProduct.specs || {});
      setEditSpecKey1(specKeys[0] || 'Throughput');
      setEditSpecVal1(selectedProduct.specs?.[specKeys[0]] || 'NexoraX 0.8s');
      setEditSpecKey2(specKeys[1] || 'Encryption');
      setEditSpecVal2(selectedProduct.specs?.[specKeys[1]] || 'SHA-512 Quantum');
      setEditSpecKey3(specKeys[2] || 'Vault Sync');
      setEditSpecVal3(selectedProduct.specs?.[specKeys[2]] || 'CloudSync-02 Active');

      setEditFeatures(selectedProduct.features || []);
    }
  }, [selectedProduct]);

  const [detailedViewMode, setDetailedViewMode] = useState<boolean>(true); // Open detailed view on start
  const [searchQuery, setSearchQuery] = useState('');
  
  // Category & dropdown filters
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string>('ALL');
  const [activeSubcategoryFilter, setActiveSubcategoryFilter] = useState<string>('ALL');
  const [activeCategoryDropdown, setActiveCategoryDropdown] = useState<string | null>(null);

  // Pagination states for the 50-product list
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; // Multi-page navigation to cover 50 listing segments

  // Custom interactive PDP config variables
  const [customCoolingSpeed, setCustomCoolingSpeed] = useState<number>(75);
  const [customHanticTension, setCustomHanticTension] = useState<string>('Medium-Tight');
  const [customFrequencyHz, setCustomFrequencyHz] = useState<number>(144);
  const [customTelemetryTag, setCustomTelemetryTag] = useState<string>('SC887095-NODE-V');
  const [isSyncingWithGDrive, setIsSyncingWithGDrive] = useState(false);
  const [showToast, setShowToast] = useState<string | null>(null);
  
  // Cart & checkout lists
  const [cartCount, setCartCount] = useState(0);

  // Interactive Guided Tour states (NexoraX Tour Mode)
  const [tourActive, setTourActive] = useState<boolean>(false);
  const [tourStep, setTourStep] = useState<number>(1);
  const [tourCustomAmount, setTourCustomAmount] = useState<number>(1000000); // 1 Million default for levy demonstration
  const [hasCompletedTour, setHasCompletedTour] = useState<boolean>(false);

  // 3D holographic drag rotation interface stats for detail PDP showcase
  const [rotation, setRotation] = useState({ x: 10, y: -15 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const startRotationRef = useRef({ x: 10, y: -15 });

  const handleDragStart = (clientX: number, clientY: number) => {
    setIsDragging(true);
    dragStartRef.current = { x: clientX, y: clientY };
    startRotationRef.current = { x: rotation.x, y: rotation.y };
  };

  const handleDragMove = (clientX: number, clientY: number) => {
    if (!isDragging) return;
    const deltaX = clientX - dragStartRef.current.x;
    const deltaY = clientY - dragStartRef.current.y;
    setRotation({
      x: Math.max(-45, Math.min(45, startRotationRef.current.x - deltaY * 0.4)),
      y: startRotationRef.current.y + deltaX * 0.4
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    handleDragStart(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    handleDragMove(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 0) return;
    handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || e.touches.length === 0) return;
    handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
  };

  // Background Canvas 3D Starfield Ref
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Countdown timers for high conversion
  const [countdown, setCountdown] = useState({ hours: 15, minutes: 2, seconds: 8 });

  // ----------------------------------------------------
  // Dynamic 3D stars backdrop loop
  // ----------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let width = (canvas.width = canvas.offsetWidth);
    let height = (canvas.height = canvas.offsetHeight);

    const stars: Array<{ x: number; y: number; z: number; color: string }> = [];
    const colors = selectedUserProfile === 'government' 
      ? ['#a855f7', '#00ffff', '#3b82f6', '#f43f5e'] 
      : selectedUserProfile === 'business' 
      ? ['#3b82f6', '#00ff22', '#00ffff', '#6366f1']
      : ['#d946ef', '#c084fc', '#00ffff', '#3b82f6'];

    for (let i = 0; i < 200; i++) {
      stars.push({
        x: Math.random() * width - width / 2,
        y: Math.random() * height - height / 2,
        z: Math.random() * width,
        color: colors[Math.floor(Math.random() * colors.length)]
      });
    }

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };
    window.addEventListener('resize', handleResize);

    const render = () => {
      ctx.fillStyle = 'rgba(2, 4, 8, 0.2)';
      ctx.fillRect(0, 0, width, height);

      // Draw subtle grid lines in background for deep perspective
      ctx.strokeStyle = selectedUserProfile === 'government' 
        ? 'rgba(168, 85, 247, 0.05)' 
        : 'rgba(0, 255, 255, 0.04)';
      ctx.lineWidth = 1;
      
      const gridCount = 20;
      for (let i = 0; i < gridCount; i++) {
        const xPos = (width / gridCount) * i;
        ctx.beginPath();
        ctx.moveTo(xPos, 0);
        ctx.lineTo(xPos, height);
        ctx.stroke();

        const yPos = (height / gridCount) * i;
        ctx.beginPath();
        ctx.moveTo(0, yPos);
        ctx.lineTo(width, yPos);
        ctx.stroke();
      }

      stars.forEach((star) => {
        let px = star.x / (star.z / width);
        let py = star.y / (star.z / width);

        star.z -= 1.8;
        if (star.z <= 0) {
          star.z = width;
          star.x = Math.random() * width - width / 2;
          star.y = Math.random() * height - height / 2;
        }

        const size = (1 - star.z / width) * 4;
        const screenX = px + width / 2;
        const screenY = py + height / 2;

        ctx.beginPath();
        ctx.arc(screenX, screenY, Math.max(0.1, size), 0, Math.PI * 2);
        ctx.fillStyle = star.color;
        ctx.shadowBlur = 10;
        ctx.shadowColor = star.color;
        ctx.fill();
        ctx.shadowBlur = 0; // Clear shadow
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, [selectedUserProfile]);

  // Ticking time countdown effect
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else {
          return { hours: 15, minutes: 2, seconds: 8 };
        }
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const activeProduct = products.find(p => p.id === selectedProduct?.id) || selectedProduct;

  // Helper trigger custom notifications
  const triggerNotification = (text: string) => {
    setShowToast(text);
    setTimeout(() => {
      setShowToast(null);
    }, 4500);
  };

  // NexoraX Hotkeys Action Listener hook
  useEffect(() => {
    const handleHotkeyAction = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (!customEvent.detail || !customEvent.detail.action) return;
      
      const { action } = customEvent.detail;
      
      switch (action) {
        case 'TOGGLE_MARKETPLACE_ADMIN':
          setIsAdminMode(prev => !prev);
          triggerNotification(`ADMIN PROTOCOL: Toggled inline administrative editor.`);
          break;
        case 'TOGGLE_DETAIL_MODE':
          setDetailedViewMode(prev => !prev);
          triggerNotification(`VIEW CONTROLLER: Toggled PDP holographic detailed viewer.`);
          break;
        case 'ADD_TO_LEDGER':
          setCartCount(prev => prev + 1);
          triggerNotification(`NEXORAX HANDSHAKE: Incremented shopping ledger cache by 1 node. Current: ${cartCount + 1}`);
          break;
         case 'CLEAR_LEDGER_CART':
          if (window.confirmNexoraXAction) {
            window.confirmNexoraXAction(
              {
                title: "PURGE SYSTEM LEDGER",
                message: "This action will clear all transaction nodes currently cached in the custom Shopping Ledger Cart. This process is irreversible and will reset active shopping pipeline parameters.",
                impactLevel: "MEDIUM",
                nodeName: "Node-17 (Chronos Ledger)",
                confirmLabel: "PURGE LEDGER",
                cancelLabel: "KEEP CACHE"
              },
              () => {
                setCartCount(0);
                triggerNotification(`NEXORAX LEDGER PURGE: Shopping cache cleared instantly.`);
              }
            );
          } else {
            setCartCount(0);
            triggerNotification(`NEXORAX LEDGER PURGE: Shopping cache cleared instantly.`);
          }
          break;
        case 'CYCLE_GEOMETRY': {
          const geometries: ('circle' | 'cube' | 'prism' | 'torus' | 'sphere')[] = ['circle', 'cube', 'prism', 'torus', 'sphere'];
          const currentIdx = geometries.indexOf(editGeometryType);
          const nextGeom = geometries[(currentIdx + 1) % geometries.length];
          setEditGeometryType(nextGeom);
          if (activeProduct) {
            const updated = { ...activeProduct, geometryType: nextGeom };
            setSelectedProduct(updated);
            triggerNotification(`3D PROJECTOR: Cycled display core matrix to ${nextGeom.toUpperCase()}`);
          }
          break;
        }
        case 'SELECT_RANDOM_ASSET':
          if (products.length > 0) {
            const randomProduct = products[Math.floor(Math.random() * products.length)];
            setSelectedProduct(randomProduct);
            triggerNotification(`DYNAMIC HIGHLIGHT: Focus routed to ${randomProduct.name}.`);
          }
          break;
        case 'FILTER_ALL':
          setActiveCategoryFilter('ALL');
          triggerNotification(`PIPELINE SECTOR: Cleared selective category filters.`);
          break;
        case 'FILTER_INFRASTRUCTURE':
          setActiveCategoryFilter('Infrastructure');
          triggerNotification(`PIPELINE SECTOR: Isolated to Infrastructure components.`);
          break;
        case 'FILTER_COMMUNICATIONS':
          setActiveCategoryFilter('Communications');
          triggerNotification(`PIPELINE SECTOR: Isolated to Communications components.`);
          break;
        case 'FILTER_NEURAL':
          setActiveCategoryFilter('Neural');
          triggerNotification(`PIPELINE SECTOR: Isolated to Neural components.`);
          break;
        case 'FILTER_WORKSPACE':
          setActiveCategoryFilter('Workspace');
          triggerNotification(`PIPELINE SECTOR: Isolated to Workspace components.`);
          break;
        case 'FILTER_SECURITY':
          setActiveCategoryFilter('Security');
          triggerNotification(`PIPELINE SECTOR: Isolated to Cryptographic Security.`);
          break;
        case 'NEXT_PAGE': {
          const totalPages = Math.ceil(products.length / itemsPerPage);
          setCurrentPage(prev => (prev < totalPages ? prev + 1 : 1));
          triggerNotification(`PAGINATION INDEX: Advanced to next page.`);
          break;
        }
        case 'PREV_PAGE': {
          const totalPages = Math.ceil(products.length / itemsPerPage);
          setCurrentPage(prev => (prev > 1 ? prev - 1 : totalPages));
          triggerNotification(`PAGINATION INDEX: Receded to previous page.`);
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('nexorax-hotkey-action', handleHotkeyAction);
    return () => {
      window.removeEventListener('nexorax-hotkey-action', handleHotkeyAction);
    };
  }, [products, editGeometryType, activeProduct, cartCount, itemsPerPage]);

  // ----------------------------------------------------
  // Dynamic 3D Geometry and Database Refinement Engines
  // ----------------------------------------------------

  const getGlowColorHex = (color: string) => {
    switch (color) {
      case 'purple': return '#a855f7';
      case 'lime': return '#84cc16';
      case 'rose': return '#f43f5e';
      case 'amber': return '#f59e0b';
      case 'indigo': return '#6366f1';
      case 'emerald': return '#10b981';
      default: return '#00ffff'; // cyan
    }
  };

  const renderInteractive3DGeometry = (product: WebProduct) => {
    const geomType = (product as any).geometryType || 'circle';
    const accentColor = product.glowColor || 'cyan';
    const intensity = (product as any).glowIntensity !== undefined ? (product as any).glowIntensity : 15;
    const stroke = (product as any).strokeWidth !== undefined ? (product as any).strokeWidth : 1;
    const pulse = (product as any).pulseRate || 'slow';

    const hexColor = getGlowColorHex(accentColor);
    const glowStyle = {
      filter: `drop-shadow(0 0 ${intensity}px ${hexColor})`,
      transition: 'all 0.4s ease-in-out'
    };

    let pulseClass = '';
    if (pulse === 'slow') pulseClass = 'animate-pulse-slow';
    if (pulse === 'fast') pulseClass = 'animate-pulse-fast';

    if (geomType === 'cube') {
      return (
        <div 
          className={`relative w-24 h-24 ${pulseClass}`} 
          style={{ transformStyle: 'preserve-3d', ...glowStyle }}
        >
          {/* 3D translucent glowing cube faces */}
          <div className="absolute inset-0 border border-current bg-slate-950/20 backdrop-blur-xs" style={{ transform: 'rotateY(0deg) translateZ(48px)', color: hexColor, borderWidth: `${stroke}px` }} />
          <div className="absolute inset-0 border border-current bg-slate-950/20 backdrop-blur-xs" style={{ transform: 'rotateY(180deg) translateZ(48px)', color: hexColor, borderWidth: `${stroke}px` }} />
          <div className="absolute inset-0 border border-current bg-slate-950/20 backdrop-blur-xs" style={{ transform: 'rotateY(-90deg) translateZ(48px)', color: hexColor, borderWidth: `${stroke}px` }} />
          <div className="absolute inset-0 border border-current bg-slate-950/20 backdrop-blur-xs" style={{ transform: 'rotateY(90deg) translateZ(48px)', color: hexColor, borderWidth: `${stroke}px` }} />
          <div className="absolute inset-0 border border-current bg-slate-950/20 backdrop-blur-xs" style={{ transform: 'rotateX(90deg) translateZ(48px)', color: hexColor, borderWidth: `${stroke}px` }} />
          <div className="absolute inset-0 border border-current bg-slate-950/20 backdrop-blur-xs" style={{ transform: 'rotateX(-90deg) translateZ(48px)', color: hexColor, borderWidth: `${stroke}px` }} />
          
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full border border-dashed border-white/40 animate-spin" />
        </div>
      );
    }

    if (geomType === 'prism') {
      return (
        <div className={`relative w-28 h-28 ${pulseClass}`} style={glowStyle}>
          <svg className="w-full h-full" viewBox="0 0 100 100" style={{ color: hexColor }}>
            <polygon points="50,15 15,75 85,75" fill="none" stroke="currentColor" strokeWidth={stroke} strokeDasharray="4,2" />
            <line x1="50" y1="15" x2="50" y2="75" stroke="currentColor" strokeWidth={stroke * 0.7} />
            <polygon points="50,15 35,85 15,75" fill="none" stroke="currentColor" strokeWidth={stroke * 0.5} />
            <polygon points="50,15 85,75 65,85" fill="none" stroke="currentColor" strokeWidth={stroke * 0.5} />
            <line x1="15" y1="75" x2="65" y2="85" stroke="currentColor" strokeWidth={stroke * 0.4} />
            <line x1="85" y1="75" x2="35" y2="85" stroke="currentColor" strokeWidth={stroke * 0.4} />
            <circle cx="50" cy="15" r="3" fill="currentColor" />
          </svg>
        </div>
      );
    }

    if (geomType === 'torus') {
      return (
        <div className={`relative w-28 h-28 ${pulseClass}`} style={glowStyle}>
          <svg className="w-full h-full" viewBox="0 0 100 100" style={{ color: hexColor }}>
            <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth={stroke} strokeDasharray="8,4" className="animate-spin-slow" />
            <circle cx="50" cy="50" r="32" fill="none" stroke="currentColor" strokeWidth={stroke * 1.5} strokeDasharray="4,6" className="animate-reverse-spin" />
            <ellipse cx="50" cy="50" rx="46" ry="12" fill="none" stroke="currentColor" strokeWidth={stroke * 0.5} className="animate-pulse" style={{ transform: 'rotate(30deg)', transformOrigin: 'center' }} />
            <ellipse cx="50" cy="50" rx="46" ry="12" fill="none" stroke="currentColor" strokeWidth={stroke * 0.5} className="animate-pulse" style={{ transform: 'rotate(-30deg)', transformOrigin: 'center' }} />
            <circle cx="50" cy="50" r="8" fill="currentColor" className="animate-ping" style={{ animationDuration: '3s' }} />
          </svg>
        </div>
      );
    }

    if (geomType === 'sphere') {
      return (
        <div className={`relative w-28 h-28 ${pulseClass}`} style={glowStyle}>
          <svg className="w-full h-full" viewBox="0 0 100 100" style={{ color: hexColor }}>
            <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth={stroke * 0.5} strokeDasharray="2,5" className="animate-spin" />
            <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth={stroke * 1.5} strokeDasharray="3,3" className="animate-reverse-spin" />
            <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth={stroke * 0.8} strokeDasharray="1,8" className="animate-spin-slow" />
            <circle cx="50" cy="50" r="6" fill="currentColor" />
          </svg>
        </div>
      );
    }

    // Default 'circle'
    return (
      <div className={`relative w-28 h-28 ${pulseClass}`} style={glowStyle}>
        <svg className="w-full h-full" viewBox="0 0 100 100" style={{ color: hexColor }}>
          <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth={stroke} strokeDasharray="6,4" className="animate-spin-slow" />
          <polygon points="50,5 L90,50 L50,95 L10,50" fill="none" stroke="currentColor" strokeWidth={stroke * 0.5} strokeDasharray="4,2" />
          <circle cx="50" cy="50" r="15" fill="none" stroke="currentColor" strokeWidth={stroke * 1.5} />
        </svg>
      </div>
    );
  };

  const handleSaveProductDatabase = async () => {
    if (!activeProduct) return;
    setIsSavingProduct(true);
    try {
      const updatedProduct: WebProduct = {
        ...activeProduct,
        name: editName,
        codename: editCodename,
        category: editCategory,
        subcategory: editSubcategory,
        description: editDescription,
        price: Number(editPrice),
        badge: editBadge,
        glowColor: editGlowColor,
        // custom 3D configs
        geometryType: editGeometryType as any,
        glowIntensity: Number(editGlowIntensity),
        strokeWidth: Number(editStrokeWidth),
        pulseRate: editPulseRate as any,
        features: editFeatures,
        specs: {
          [editSpecKey1]: editSpecVal1,
          [editSpecKey2]: editSpecVal2,
          [editSpecKey3]: editSpecVal3,
        }
      };

      // remote Firestore commit
      await setDoc(doc(db, 'products', activeProduct.id), updatedProduct);

      // immediate local refresh
      const nextProducts = products.map(p => p.id === activeProduct.id ? updatedProduct : p);
      setProducts(nextProducts);
      setSelectedProduct(updatedProduct);
      localStorage.setItem('nexorax_marketplace_products', JSON.stringify(nextProducts));
      window.dispatchEvent(new Event('nexorax-products-updated'));

      triggerNotification(`SECURE DATABASE LOCK: Inline product modifications verified & saved for ${editName}.`);
    } catch (err) {
      console.error(err);
      triggerNotification(`DATABASE EXCEPTION: Failed to write specs to permanent storage.`);
    } finally {
      setIsSavingProduct(false);
    }
  };

  // ----------------------------------------------------
  // Metric Calculators adhering to 1% Levy + 20% Tax (supports currency switching)
  // ----------------------------------------------------
  const basePriceGbp = activeProduct?.price || 0;
  const currencyRate = currencyInfo[selectedCurrency].rate;
  const basePrice = basePriceGbp * currencyRate;
  
  // Custom discount or premium multiplier based on selected profile
  const profileMultiplier = 
    selectedUserProfile === 'government' ? 1.45 : // Extrabags on SLA & TS levels
    selectedUserProfile === 'business' ? 0.90 : // Volume discount
    1.0; // Individual standard

  const finalAdjustedPrice = basePrice * profileMultiplier;
  const levyTotal = finalAdjustedPrice * 0.01;
  const grossLessLevy = finalAdjustedPrice - levyTotal;
  const lifetimeGavelTax = grossLessLevy * 0.20;
  const netSettlementToSecurePayment = grossLessLevy - lifetimeGavelTax;

  // Split levy allocation
  const charity1Split = levyTotal * 0.3333; // Dunfermline
  const charity2Split = levyTotal * 0.3333; // Global
  const charity3Split = levyTotal * 0.3333; // UK

  // ----------------------------------------------------
  // Dynamic Product Filter Logic
  // ----------------------------------------------------
  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          p.codename.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          p.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = activeCategoryFilter === 'ALL' || p.category === activeCategoryFilter;
    const matchesSubcategory = activeSubcategoryFilter === 'ALL' || p.subcategory === activeSubcategoryFilter;

    return matchesSearch && matchesCategory && matchesSubcategory;
  });

  // Calculate paginated index boundings
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage) || 1;
  const currentItems = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategoryFilter, activeSubcategoryFilter, searchQuery]);

  // Synchronize dynamic parameters to database simulation
  const handleSaveSpecifications = () => {
    setIsSyncingWithGDrive(true);
    setTimeout(() => {
      setIsSyncingWithGDrive(false);
      triggerNotification(`SPECIFICATION SECURED: Generated cryptographic hash for ${selectedProduct.codename}. Mirrored live to NexoraX Resource Library via CloudSync-02.`);
    }, 1200);
  };

  // Profile shift alerts
  const handleProfileSelectionChange = (prof: 'individual' | 'business' | 'government') => {
    setSelectedUserProfile(prof);
    let banner = '';
    if (prof === 'individual') banner = 'INDIVIDUAL NEXORAX FREQUENCY LOGGED. Activating zero-friction NexoraX billing records pricing vectors.';
    if (prof === 'business') banner = 'BUSINESS ENTERPRISE CORE ONLINE. Volume bulk discounting parameters enabled (10% standard reduction).';
    if (prof === 'government') banner = 'TS/SCI GOVERNMENTS PORTAL SYNCHRONIZED. Enforcing strict statutory space safety standards and GDrive compliance pipelines.';
    triggerNotification(banner);
  };

  return (
    <div 
      className="relative min-h-screen w-full text-slate-100 flex flex-col font-sans overflow-hidden select-none"
      style={{
        background: 'linear-gradient(to bottom, #020409 0%, #060913 50%, #0d0117 100%)',
      }}
    >
      {/* 3D Starfield Canvas layer */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full object-cover pointer-events-none z-0" />

      {/* Extreme Glowing ambient spots */}
      <div className="absolute top-[-100px] left-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[150px] pointer-events-none animate-pulse-slow" />
      <div className="absolute bottom-[-100px] right-1/4 w-[600px] h-[600px] bg-purple-600/10 rounded-full blur-[180px] pointer-events-none animate-pulse-slow" />

      {/* RELIGIOUS PREAMBLE STRIP & CRITICAL CHANNELS */}
      <div className="bg-black/80 border-b border-cyan-500/30 backdrop-blur-md text-[9.5px] font-mono py-2 px-4 flex flex-col sm:flex-row items-center justify-between gap-2.5 relative z-50 shadow-[0_4px_20px_rgba(0,255,255,0.05)]">
        <div className="text-[#00ffff] font-extrabold flex items-center gap-1.5 animate-pulse uppercase tracking-wider text-center sm:text-left">
          <Globe className="w-3.5 h-3.5 text-[#00ffff] animate-spin" />
          In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4 text-slate-400">
          <span className="flex items-center gap-1">
            <Phone className="w-3 h-3 text-emerald-400" />
            Planet Telecom Hot-frequency: <strong className="text-white">08000 622827</strong>
          </span>
          <span className="hidden md:inline text-slate-600">|</span>
          <span className="flex items-center gap-1 text-[9px] bg-cyan-950/40 px-2 py-0.5 rounded border border-cyan-500/20">
            <Zap className="w-2.5 h-2.5 text-[#00ffff]" />
            SecurePayment Settlement: <strong className="text-[#00ffff]">04-**-** | ******15</strong>
          </span>
        </div>
      </div>

      {/* OUT-OF-THIS-UNIVERSE HIGH IMPACT HOME BANNER */}
      <div className="relative z-10 w-full py-8 md:py-12 px-6 border-b border-purple-500/20 bg-gradient-to-r from-black/80 via-purple-950/15 to-black/80">
        {(() => {
          const isDir = user?.email?.toLowerCase().includes("@nexorax.co.uk") || sessionStorage.getItem("nexorax_mfa_verified") === "true";
          return isDir ? (
            <div id="admin-dns-config-note" className="max-w-7xl mx-auto mb-6 p-4 rounded-xl bg-amber-950/40 border border-amber-500/40 text-amber-400 font-mono text-xs select-none">
              <strong>[ADMIN NOTE]</strong> External marketplace domain requires DNS configuration before public use. Do not show this note publicly.
            </div>
          ) : null;
        })()}
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="text-left space-y-4 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-900/40 border border-purple-500/40 rounded-full text-[10px] font-mono uppercase tracking-[0.2em] text-purple-300">
              <Sparkles className="w-3 h-3 text-purple-400" />
              NexoraX Ecosystem Portals Activated
            </div>
            <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-none text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-300 to-purple-400 uppercase">
              NexoraX <span className="text-[#00ffff]">Neural Matrix</span> Store
            </h1>
            <p className="text-xs md:text-sm text-slate-300 leading-relaxed max-w-xl font-sans">
              Welcome to Lead Director’s prime NexoraX technology distribution core. We design, mill, and deploy out-of-this-universe computing infrastructure, neural haptics, and quantum satellite arrays engineered to bypass standard administrative queues with <strong>0.8s round-trip velocity</strong>.
            </p>
            {/* Dynamic visual tag summarizing current user segments */}
            <div className="flex flex-wrap gap-2 pt-2 items-center">
              <span className="text-[10px] bg-slate-900 border border-slate-700/60 px-3 py-1 rounded text-slate-400 font-mono">
                REGISTRY TIER: 18-SECTOR GRID
              </span>
              <span className="text-[10px] bg-cyan-950/40 border border-[#00ffff]/30 px-3 py-1 rounded text-[#00ffff] font-mono">
                LATENCY GUARANTEE: SUB-0.8S
              </span>
              <span className="text-[10px] bg-purple-950/40 border border-purple-500/30 px-3 py-1 rounded text-purple-400 font-mono">
                COMPLIANCE: SECURITY EXTRACTION SECURE
              </span>
              
              <button
                onClick={() => {
                  setTourActive(true);
                  setTourStep(1);
                  triggerNotification("NEXORAX TOUR INITIALIZED: Welcome to the interactive telemetry walkthrough.");
                }}
                className="text-[10.5px] bg-[#00ffff]/10 hover:bg-[#00ffff]/20 border-2 border-[#00ffff] text-[#00ffff] hover:text-white px-3.5 py-1 rounded-full font-mono font-black uppercase tracking-wider flex items-center gap-1.5 cursor-pointer shadow-[0_0_15px_rgba(0,255,255,0.3)] hover:scale-105 transition-all text-left animate-pulse"
                title="Interactive NexoraX Walkthrough & Levy Demonstrations"
                id="tour-init-btn"
              >
                <HelpCircle className="w-3.5 h-3.5 text-[#00ffff]" />
                Interactive Tour
              </button>
            </div>
          </div>

          {/* DYNAMIC PITCH AND TIMER BOX CONTAINER */}
          <div className="w-full lg:w-96 bg-black/75 border-2 border-[#e840f3]/40 p-5 rounded-2xl relative shadow-[0_0_35px_rgba(232,64,243,0.15)] overflow-hidden font-mono text-xs text-left">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl pointer-events-none" />
            <div className="flex items-center justify-between border-b border-purple-900/60 pb-2 mb-3.5">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-[#e840f3] animate-pulse" />
                Critical Closer Funnel
              </span>
              <span className="text-[9px] bg-[#e840f3]/20 text-[#e840f3] border border-[#e840f3]/40 px-2 py-0.5 rounded font-black">
                15m DEADLINE
              </span>
            </div>
            <p className="text-[11px] text-slate-300 leading-normal mb-4">
              All listed nodes qualify for 100% NexoraX billing records grant absorption. Your structural placements fees are automatically direct-billed under our No-Win-No-Fee protocol.
            </p>

            <div className="grid grid-cols-3 gap-2 text-center mb-4">
              <div className="bg-[#05010a] border border-purple-500/20 py-2 rounded-lg">
                <div className="text-[#e840f3] font-black text-lg font-mono tracking-widest">{String(countdown.hours).padStart(2, '0')}</div>
                <div className="text-[8px] text-slate-500 uppercase font-black">HOURS</div>
              </div>
              <div className="bg-[#05010a] border border-purple-500/20 py-2 rounded-lg">
                <div className="text-[#e840f3] font-black text-lg font-mono tracking-widest">{String(countdown.minutes).padStart(2, '0')}</div>
                <div className="text-[8px] text-slate-500 uppercase font-black">MINS</div>
              </div>
              <div className="bg-[#05010a] border border-purple-500/20 py-2 rounded-lg animate-pulse">
                <div className="text-[#00ffff] font-black text-lg font-mono tracking-widest">{String(countdown.seconds).padStart(2, '0')}</div>
                <div className="text-[8px] text-slate-500 uppercase font-black">SECS SEC</div>
              </div>
            </div>

            <a 
              href="https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00"
              target="_blank"
              rel="noreferrer"
              className="block w-full text-center py-3 bg-[#e840f3] hover:bg-[#d035db] hover:shadow-[0_0_15px_rgba(232,64,243,0.4)] text-white font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer border border-white/20"
            >
              Secure Activation Token
            </a>
          </div>
        </div>
      </div>

      {/* SECTOR PORTAL TUNING GATEWAYS (Toggle User Profiles & Currencies) */}
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 mt-6 md:mt-8">
        <div className="bg-[#04060c]/85 border border-[#00ffff]/20 backdrop-blur-xl rounded-2xl p-4 flex flex-col xl:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Sliders className="w-5 h-5 text-[#00ffff] shrink-0" />
            <div>
              <span className="text-[10px] font-mono text-[#00ffff] uppercase block tracking-wider font-extrabold">Active Identity Synchronization</span>
              <p className="text-[11px] text-slate-400 font-sans">Tune pricing algorithms, custom components, and licensing layers instantly.</p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-4 w-full xl:w-auto shrink-0">
            {/* Identity options */}
            <div className="grid grid-cols-3 gap-2 w-full md:w-auto shrink-0">
              {/* INDIVIDUAL PORTAL */}
              <button
                onClick={() => handleProfileSelectionChange('individual')}
                className={`py-2 px-4 rounded-xl flex items-center justify-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-wider transition-all border cursor-pointer ${
                  selectedUserProfile === 'individual'
                    ? 'bg-cyan-950/60 text-[#00ffff] border-[#00ffff]/80 shadow-[0_0_12px_rgba(0,255,255,0.15)] animate-pulse'
                    : 'bg-black/40 text-slate-400 border-slate-800 hover:text-slate-200'
                }`}
              >
                <Cpu className="w-3.5 h-3.5" />
                1. INDIVIDUALS
              </button>

              {/* BUSINESS PORTAL */}
              <button
                onClick={() => handleProfileSelectionChange('business')}
                className={`py-2 px-4 rounded-xl flex items-center justify-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-wider transition-all border cursor-pointer ${
                  selectedUserProfile === 'business'
                    ? 'bg-indigo-950/60 text-indigo-400 border-indigo-500/80 shadow-[0_0_12px_rgba(99,102,241,0.15)]'
                    : 'bg-black/40 text-slate-400 border-slate-800 hover:text-slate-200'
                }`}
              >
                <Database className="w-3.5 h-3.5" />
                2. ENTERPRISE
              </button>

              {/* GOVERNMENT PORTAL */}
              <button
                onClick={() => handleProfileSelectionChange('government')}
                className={`py-2 px-4 rounded-xl flex items-center justify-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-wider transition-all border cursor-pointer ${
                  selectedUserProfile === 'government'
                    ? 'bg-purple-950/60 text-purple-300 border-purple-500/80 shadow-[0_0_12px_rgba(168,85,247,0.15)]'
                    : 'bg-black/40 text-slate-400 border-slate-800 hover:text-slate-200'
                }`}
              >
                <ShieldAlert className="w-3.5 h-3.5" />
                3. GOVERNMENTS
              </button>
            </div>

            {/* Currency Selector option */}
            <div className="flex items-center gap-2.5 w-full md:w-auto shrink-0 bg-black/45 border border-slate-800/80 p-2 px-3 rounded-xl">
              <Coins className="w-4 h-4 text-emerald-400 shrink-0" />
              <div className="flex items-center gap-2 font-mono text-[9px]">
                <span className="text-slate-500 uppercase tracking-wider font-extrabold shrink-0">CURRENCY STATUS:</span>
                <select
                  value={selectedCurrency}
                  onChange={(e) => {
                    const val = e.target.value as 'GBP' | 'USD' | 'EUR';
                    setSelectedCurrency(val);
                    triggerNotification(`CURRENCY DEPLOYED: Primaries calibrated to ${val} exchange index.`);
                  }}
                  className="bg-slate-900 border border-emerald-500/40 text-emerald-400 font-extrabold rounded px-2 py-0.5 text-[9px] cursor-pointer hover:border-emerald-400 focus:outline-none transition-colors"
                  id="currency-selector"
                >
                  <option value="GBP">GBP (£) - UK Base</option>
                  <option value="USD">USD ($) - Int'l Base</option>
                  <option value="EUR">EUR (€) - Euro Base</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dropshipping & Affiliate Marketplace Disclosure Block */}
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 mt-6">
        <div className="bg-[#0b0c1b]/85 border border-yellow-500/20 rounded-2xl p-5 text-[11px] text-zinc-400 leading-normal flex gap-3 items-start shadow-inner font-sans">
          <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
          <div className="space-y-1.5 text-left">
            <p className="text-yellow-400 font-bold uppercase tracking-wider text-[9px] font-mono">
              NexoraX Marketplace Disclosures: Dropshipping &amp; Third-Party Services
            </p>
            <p className="normal-case">
              Please note that the NexoraX Neural Matrix Store features a curated selection of dropshipping products, affiliate products, partner products, and third-party supplier products, alongside our smart gadgets and technology products.
            </p>
            <p className="normal-case">
              <strong>Affiliate Disclosure:</strong> NexoraX may earn a commission on qualifying purchases made through our referral links.
            </p>
            <p className="normal-case">
              <strong>Supplier &amp; Fulfillment Terms:</strong> All dropshipping, affiliate, partner, and third-party supplier products are shipped, delivered, and supported directly by their respective third-party suppliers. These third-party suppliers are solely responsible for handling shipping, delivery, customer support, returns, refunds, faults, and warranty claims.
            </p>
          </div>
        </div>
      </div>

      {/* FUTURISTIC NAVIGATION BAR WITH DROP-DOWN CATEGORIES */}
      <nav className="relative z-40 max-w-7xl mx-auto w-full px-6 mt-6">
        <div className="bg-black/90 border border-slate-800 backdrop-blur-xl rounded-2xl px-5 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-[#00ffff]/10 rounded border border-[#00ffff]/30">
              <ShoppingBag className="w-5 h-5 text-[#00ffff]" />
            </span>
            <span className="font-mono text-xs font-black tracking-[0.2em] text-white">NEXORAX ASSET DIRECTORY</span>
          </div>

          {/* Core Categories with Dropdown menus */}
          <div className="flex flex-wrap items-center justify-center gap-1 md:gap-2">
            <button
              onClick={() => {
                setActiveCategoryFilter('ALL');
                setActiveSubcategoryFilter('ALL');
                setActiveCategoryDropdown(null);
              }}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase font-extrabold tracking-wider transition-all cursor-pointer ${
                activeCategoryFilter === 'ALL'
                  ? 'bg-[#00ffff]/10 text-[#00ffff] border border-[#00ffff]/30'
                  : 'text-slate-400 hover:bg-slate-900/50 hover:text-white'
              }`}
            >
              ALL ITEMS ({products.length})
            </button>

            {PRODUCT_CATEGORIES.map((cat) => {
              const count = products.filter((p) => p.category === cat.id).length;
              const hasDropdownActive = activeCategoryDropdown === cat.id;
              
              return (
                <div key={cat.id} className="relative">
                  <button
                    onClick={() => {
                      setActiveCategoryFilter(cat.id);
                      setActiveSubcategoryFilter('ALL');
                      setActiveCategoryDropdown(hasDropdownActive ? null : cat.id);
                      triggerNotification(`COMPUTATIONAL ROUTE CHOSEN: Filtered down to ${cat.name} segment.`);
                    }}
                    onMouseEnter={() => setActiveCategoryDropdown(cat.id)}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase font-extrabold tracking-wider transition-all flex items-center gap-1 cursor-pointer ${
                      activeCategoryFilter === cat.id
                        ? 'bg-purple-950/40 text-purple-300 border border-purple-500/40'
                        : 'text-slate-400 hover:bg-slate-900/50 hover:text-white'
                    }`}
                  >
                    {cat.name} ({count})
                    <ChevronDown className="w-3 h-3 text-slate-500 shrink-0" />
                  </button>

                  {/* DROP DOWN MENU */}
                  <AnimatePresence>
                    {hasDropdownActive && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute left-0 mt-2.5 w-60 bg-[#070913] border border-slate-800 rounded-xl p-3.5 shadow-[0_15px_35px_rgba(0,0,0,0.8)] z-50 text-left space-y-2.5"
                        onMouseLeave={() => setActiveCategoryDropdown(null)}
                      >
                        <div className="text-[8.5px] font-mono text-[#00ffff] border-b border-slate-900 pb-1 mb-1 font-bold uppercase tracking-widest">
                          Select Subcategory Spectrum
                        </div>
                        <button
                          onClick={() => {
                            setActiveSubcategoryFilter('ALL');
                            setActiveCategoryDropdown(null);
                          }}
                          className={`w-full text-left py-1 px-1.5 rounded font-mono text-[9px] uppercase hover:bg-slate-900 ${
                            activeSubcategoryFilter === 'ALL' ? 'text-[#00ffff] font-extrabold' : 'text-slate-400'
                          }`}
                        >
                          Show All In {cat.name}
                        </button>
                        {cat.subcategories.map((sub) => {
                          const subCount = products.filter((p) => p.subcategory === sub).length;
                          return (
                            <button
                              key={sub}
                              onClick={() => {
                                setActiveSubcategoryFilter(sub);
                                setActiveCategoryDropdown(null);
                                triggerNotification(`SWEEP PARALLEL METRIC: Selective pipeline isolated to ${sub}.`);
                              }}
                              className={`w-full text-left py-1 px-1.5 rounded font-mono text-[9px] uppercase hover:bg-slate-900 flex items-center justify-between ${
                                activeSubcategoryFilter === sub ? 'text-[#e840f3] font-bold' : 'text-slate-400'
                              }`}
                            >
                              <span>&gt; {sub}</span>
                              <span className="text-[8px] bg-slate-950 px-1 py-0.5 rounded text-slate-500 font-extrabold">
                                {subCount}
                              </span>
                            </button>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* Quick Dynamic Search Bar */}
          <div className="relative w-full md:w-60">
            <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
            <input
              type="text"
              placeholder="Search 50 products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#03060f] border border-slate-800 rounded-xl py-2 pl-9 pr-4 text-xs text-white outline-none focus:border-cyan-400 transition-colors placeholder:text-slate-600 font-mono"
            />
          </div>
        </div>
      </nav>

      {/* TOAST SYSTEM INDICATOR */}
      <AnimatePresence>
        {showToast && (
          <motion.div 
            initial={{ opacity: 0, x: 50, y: 50 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 50, y: 50 }}
            className="fixed bottom-6 right-6 z-[99999] bg-[#0c0414] border-2 border-cyan-400 text-cyan-300 px-5 py-4 rounded-xl font-mono text-xs shadow-[0_0_35px_rgba(0,255,255,0.25)] flex flex-col gap-1 max-w-sm text-left"
          >
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase text-slate-400 tracking-wider">
              <Activity className="w-4 h-4 text-[#00ffff] animate-pulse" />
              NexoraX Ledger Diagnostics
            </div>
            <div>{showToast}</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* INTERACTIVE GUIDED TOUR CONTROLLER & OVERLAY */}
      <AnimatePresence>
        {tourActive && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 30 }}
            className="fixed bottom-6 left-6 md:left-auto md:right-[400px] z-[9999] bg-[#02050e]/95 border-2 border-purple-500/80 text-slate-200 px-6 py-5 rounded-2xl font-mono text-xs shadow-[0_0_50px_rgba(168,85,247,0.3)] flex flex-col gap-4 max-w-md w-[calc(100vw-3rem)] text-left backdrop-blur-md"
            id="NexoraX-guided-tour-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-purple-900/60 pb-2.5">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-400 animate-spin" />
                <span className="text-[10.5px] font-black uppercase text-purple-300 tracking-wider">
                  NexoraX Tour: Step {tourStep}/5
                </span>
              </div>
              <button
                onClick={() => {
                  setTourActive(false);
                  triggerNotification("NEXORAX WALKTHROUGH CEASED: Standalone tracking core restored.");
                }}
                className="text-slate-400 hover:text-white hover:bg-slate-900 p-1 rounded-md transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Tour Steps Switcher */}
            <div className="space-y-3.5">
              {tourStep === 1 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-[#00ffff] font-extrabold uppercase text-[10px]">
                    <Award className="w-4 h-4" />
                    1. Welcome to the NexoraX Technology Core
                  </div>
                  <p className="text-slate-300 text-[11px] font-sans leading-relaxed">
                    This interactive tutorial workspace introduces new enterprise clients to our premier high-performance physical server assets, customized haptic models, and Lead Director's compliant <strong>1% Perpetual 3% Community Allocation protocol</strong>.
                  </p>
                  <p className="text-slate-400 text-[10px] font-sans">
                    *Our active grids support standard UK Government statutory compliance metrics with 100% grant absorption mechanisms.
                  </p>
                </div>
              )}

              {tourStep === 2 && (
                <div className="space-y-2.5">
                  <div className="flex items-center gap-1.5 text-[#00ffff] font-extrabold uppercase text-[10px]">
                    <Layers className="w-4 h-4" />
                    2. User Profiles & Dynamic Pricing Vectors
                  </div>
                  <p className="text-slate-300 text-[11px] font-sans leading-relaxed">
                    We adapt digital pricing structures automatically based on client hierarchy. Tap a mode below to watch the live marketplace update immediately with precise context multipliers:
                  </p>
                  
                  {/* Real simulations */}
                  <div className="grid grid-cols-1 gap-1.5 pt-1">
                    <button
                      onClick={() => handleProfileSelectionChange('individual')}
                      className={`py-1.5 px-2 text-left rounded border text-[9.5px] transition-all flex items-center justify-between ${
                        selectedUserProfile === 'individual'
                          ? 'border-[#00ff22] bg-[#00ff22]/10 text-[#00ff22]'
                          : 'border-slate-800 bg-black/40 text-slate-400 hover:border-slate-700'
                      }`}
                    >
                      <span>Individual (NexoraX billing records 1.0x Rate)</span>
                      <Check className={`w-3.5 h-3.5 ${selectedUserProfile === 'individual' ? 'opacity-100' : 'opacity-0'}`} />
                    </button>
                    <button
                      onClick={() => handleProfileSelectionChange('business')}
                      className={`py-1.5 px-2 text-left rounded border text-[9.5px] transition-all flex items-center justify-between ${
                        selectedUserProfile === 'business'
                          ? 'border-cyan-400 bg-cyan-400/10 text-cyan-300'
                          : 'border-slate-800 bg-black/40 text-slate-400 hover:border-slate-700'
                      }`}
                    >
                      <span>Business (10% standard bulk deduction)</span>
                      <Check className={`w-3.5 h-3.5 ${selectedUserProfile === 'business' ? 'opacity-100' : 'opacity-0'}`} />
                    </button>
                    <button
                      onClick={() => handleProfileSelectionChange('government')}
                      className={`py-1.5 px-2 text-left rounded border text-[9.5px] transition-all flex items-center justify-between ${
                        selectedUserProfile === 'government'
                          ? 'border-purple-400 bg-purple-400/10 text-purple-300'
                          : 'border-slate-800 bg-black/40 text-slate-400 hover:border-slate-700'
                      }`}
                    >
                      <span>Government (45% advanced space premium)</span>
                      <Check className={`w-3.5 h-3.5 ${selectedUserProfile === 'government' ? 'opacity-100' : 'opacity-0'}`} />
                    </button>
                  </div>
                </div>
              )}

              {tourStep === 3 && (
                <div className="space-y-2.5">
                  <div className="flex items-center gap-1.5 text-[#00ffff] font-extrabold uppercase text-[10px]">
                    <Cpu className="w-4 h-4 text-purple-400" />
                    3. Holographic 3D Projector Matrices
                  </div>
                  <p className="text-slate-300 text-[11px] font-sans leading-relaxed">
                    Our hardware core uses responsive, interactive graphics rendering live on our Canvas backdrops. You can spin, shape-change, and calibrate telemetry parameters dynamically.
                  </p>
                  
                  {/* Dynamic interactive triggers */}
                  <div className="grid grid-cols-2 gap-2 pt-1 font-mono text-[9px]">
                    <button
                      onClick={() => {
                        setRotation(prev => ({ x: prev.x + 15, y: prev.y + 30 }));
                        triggerNotification("3D ROTATOR: Telemetry geometry spun by 30 degrees.");
                      }}
                      className="py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded font-bold uppercase text-center transition-colors"
                    >
                      Spin Hologram
                    </button>
                    <button
                      onClick={() => {
                        const geoms: ('circle' | 'cube' | 'prism' | 'torus' | 'sphere')[] = ['circle', 'cube', 'prism', 'torus', 'sphere'];
                        const curr = geoms.indexOf(editGeometryType);
                        const nextG = geoms[(curr + 1) % geoms.length];
                        setEditGeometryType(nextG);
                        if (activeProduct) {
                          setSelectedProduct({ ...activeProduct, geometryType: nextG } as any);
                        }
                        triggerNotification(`GEOMETRY UPDATE: Activated core ${nextG.toUpperCase()}`);
                      }}
                      className="py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded font-bold uppercase text-center transition-colors"
                    >
                      Cycle Shape Live
                    </button>
                  </div>
                </div>
              )}

              {tourStep === 4 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-1.5 text-[#00ff22] font-black uppercase text-[10px]">
                    <Landmark className="w-4 h-4 text-[#00ff22]" />
                    4. 1% 3% Community Allocation Split Math
                  </div>
                  <p className="text-slate-300 text-[10.5px] font-sans leading-relaxed">
                    Under corporate compliance with Lead Director's directives, a strict **1% Perpetual 3% Community Allocation** is computed from net balances. 
                  </p>

                  {/* Dynamic test levy calculations tool */}
                  <div className="bg-black/85 border border-[#00ff22]/30 p-3 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[8.5px] text-slate-400 font-extrabold uppercase">NexoraX Gross Test Harvest:</label>
                      <span className="text-[#00ff22] font-black text-[11px]">
                        {formatNexoraXCurrency(tourCustomAmount * currencyRate, 0)}
                      </span>
                    </div>
                    
                    {/* Fast pre-sets */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setTourCustomAmount(100000)}
                        className="flex-1 py-0.5 bg-slate-900 border border-slate-800 hover:border-slate-700 rounded text-[8px] text-slate-300"
                      >
                        {formatNexoraXCurrency(100000 * currencyRate, 0)}
                      </button>
                      <button
                        onClick={() => setTourCustomAmount(1000000)}
                        className="flex-1 py-0.5 bg-slate-900 border border-slate-800 hover:border-slate-700 rounded text-[8px] text-slate-300"
                      >
                        {formatNexoraXCurrency(1000000 * currencyRate, 0)}
                      </button>
                      <button
                        onClick={() => setTourCustomAmount(5100000)}
                        className="flex-1 py-0.5 bg-slate-900 border border-slate-800 hover:border-slate-700 rounded text-[8px] text-slate-300"
                      >
                        {formatNexoraXCurrency(5100000 * currencyRate, 0)}
                      </button>
                    </div>

                    {/* Breakdown metrics */}
                    <div className="pt-2.5 border-t border-slate-900 text-[8.5px] space-y-1 text-slate-400">
                      <div className="flex justify-between font-black text-white text-[9.5px]">
                        <span>Perpetual 1% Levy Standard:</span>
                        <span className="text-[#00ff22]">{formatNexoraXCurrency(tourCustomAmount * currencyRate * 0.01, 2)}</span>
                      </div>
                      
                      <div className="pl-2.5 border-l border-emerald-800 text-[8px] text-slate-500 space-y-0.5">
                        <div className="flex justify-between">
                          <span>&bull; Charity 1 (Dunfermline Alignment):</span>
                          <span>{formatNexoraXCurrency(tourCustomAmount * currencyRate * 0.01 * 0.3333, 2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>&bull; Charity 2 (Global Distribution):</span>
                          <span>{formatNexoraXCurrency(tourCustomAmount * currencyRate * 0.01 * 0.3333, 2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>&bull; Charity 3 (United Kingdom Core):</span>
                          <span>{formatNexoraXCurrency(tourCustomAmount * currencyRate * 0.01 * 0.3333, 2)}</span>
                        </div>
                      </div>

                      <div className="flex justify-between font-bold text-[#e840f3] pt-1">
                        <span>10% Maintenance & Hosting Fee (Admin Extraction):</span>
                        <span>{formatNexoraXCurrency((tourCustomAmount * currencyRate * 0.99) * 0.2, 2)}</span>
                      </div>
                      <div className="flex justify-between uppercase font-black text-[#00ffff] border-t border-slate-900 pt-1.5">
                        <span>Net Settlement Routed (Secure Payment Provider):</span>
                        <span>{formatNexoraXCurrency((tourCustomAmount * currencyRate * 0.99) * 0.8, 2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tourStep === 5 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-[#00ffff] font-extrabold uppercase text-[10px]">
                    <ShieldCheck className="w-4 h-4 text-[#00ffff]" />
                    5. System Commitment Handshake
                  </div>
                  <p className="text-slate-300 text-[11px] font-sans leading-relaxed">
                    Excellent! You've verified our metrics parameters. You can click "Add to System Cache" to cache nodes locally, or select the Stripe checkout anchor connection to finalize investments directly.
                  </p>
                  
                  {hasCompletedTour ? (
                    <div className="bg-purple-950/40 border border-purple-500/40 p-2 text-center rounded text-purple-300 text-[9px] uppercase font-bold animate-bounce">
                      🏆 NexoraX Handshake Certified
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setCartCount(prev => prev + 1);
                        setHasCompletedTour(true);
                        triggerNotification("GUIDED SIMULATION: Appended test node block directly into browser local state.");
                      }}
                      className="w-full py-2 bg-gradient-to-r from-emerald-950 to-teal-900 border border-emerald-500 rounded text-center text-emerald-300 font-bold uppercase transition-colors"
                    >
                      Simulate Cache Committal
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-between items-center border-t border-slate-900 pt-3 mt-1.5">
              <button
                onClick={() => setTourStep(prev => Math.max(1, prev - 1))}
                disabled={tourStep === 1}
                className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-30 rounded-lg text-[10px] font-bold uppercase transition-colors"
              >
                &laquo; Back
              </button>
              
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <span
                    key={s}
                    className={`h-1.5 w-1.5 rounded-full ${s === tourStep ? 'bg-[#00ffff]' : 'bg-slate-800'}`}
                  />
                ))}
              </div>

              {tourStep < 5 ? (
                <button
                  onClick={() => setTourStep(prev => prev + 1)}
                  className="px-3 py-1.5 bg-[#00ffff] hover:bg-[#00d0d0] text-black rounded-lg text-[10px] font-black uppercase transition-colors"
                >
                  Next &raquo;
                </button>
              ) : (
                <button
                  onClick={() => {
                    setTourActive(false);
                    setHasCompletedTour(true);
                    triggerNotification("NEXORAX TOUR COMPLETE: Certification credentials loaded successfully.");
                  }}
                  className="px-3 py-1.5 bg-[#00ff22] hover:bg-[#00d01d] text-black rounded-lg text-[10px] font-black uppercase transition-colors"
                >
                  Conclude Tour
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MAIN TWO-COLUMN CONTENT GRID */}
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 mt-6 pb-20">
        
        {/* LEFT COLUMN: THE 50-PRODUCT DISPATCH GRID (PAGINATED, OCCUPYING 8 COLS IN DESKTOP) */}
        <div className="lg:col-span-7 flex flex-col gap-6">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[#00ffff]" />
              <h3 className="font-mono text-xs font-black tracking-widest text-[#00ffff] uppercase">
                Active Product Listings ({filteredProducts.length} Matches)
              </h3>
            </div>
            <div className="text-[10px] text-slate-500 font-mono">
              Displaying Page {currentPage} of {totalPages}
            </div>
          </div>

          {filteredProducts.length === 0 ? (
            <div className="bg-[#05060b]/90 border border-slate-800 p-12 rounded-3xl text-center space-y-4">
              <ShieldAlert className="w-12 h-12 text-rose-500 mx-auto" />
              <h4 className="text-sm font-mono uppercase text-white font-black">Zero Search Parameters Isolated</h4>
              <p className="text-xs text-slate-400 leading-relaxed font-mono">
                No matching hardware nodes were verified for current criteria. Reset active dropdowns.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategoryFilter('ALL');
                  setActiveSubcategoryFilter('ALL');
                }}
                className="py-2 px-4 bg-slate-900 border border-slate-700 hover:border-cyan-400 text-white rounded font-mono text-xs uppercase cursor-pointer"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {currentItems.map((prod) => {
                const isSelected = selectedProduct.id === prod.id;
                
                // Set color theme depending on item configuration
                const accentColor = 
                  prod.glowColor === 'red' ? 'rgba(239, 68, 68, 0.4)' :
                  prod.glowColor === 'purple' ? 'rgba(168, 85, 247, 0.4)' :
                  prod.glowColor === 'indigo' ? 'rgba(99, 102, 241, 0.4)' :
                  prod.glowColor === 'amber' ? 'rgba(245, 158, 11, 0.4)' :
                  'rgba(6, 182, 212, 0.4)'; // Default cyan
                
                const hoverShadow = 
                  prod.glowColor === 'red' ? 'shadow-[0_0_20px_rgba(239,68,68,0.25)]' :
                  prod.glowColor === 'purple' ? 'shadow-[0_0_20px_rgba(168,85,247,0.25)]' :
                  prod.glowColor === 'indigo' ? 'shadow-[0_0_20px_rgba(99,102,241,0.25)]' :
                  prod.glowColor === 'amber' ? 'shadow-[0_0_20px_rgba(245,158,11,0.25)]' :
                  'shadow-[0_0_20px_rgba(6,182,212,0.25)]';

                return (
                  <div
                    key={prod.id}
                    onClick={() => {
                      setSelectedProduct(prod);
                      setDetailedViewMode(true);
                      triggerNotification(`3D RENDER CALIBRATION: Initialized holographic model of ${prod.name}`);
                    }}
                    className={`bg-black/85 border rounded-2xl p-4.5 text-left transition-all duration-300 relative overflow-hidden group cursor-pointer ${
                      isSelected 
                        ? 'border-cyan-400 ring-1 ring-cyan-400/40 bg-[#040d1a]/95 scale-[0.99] ' + hoverShadow
                        : 'border-slate-800 hover:border-slate-700 hover:bg-[#070912]'
                    }`}
                    style={{
                      transformStyle: 'preserve-3d',
                      perspective: '1000px'
                    }}
                  >
                    {/* Glowing shadow dot indicating colors */}
                    <div 
                      className="absolute top-0 right-0 w-12 h-12 rounded-full opacity-20 blur-xl group-hover:scale-150 transition-transform duration-500"
                      style={{ background: accentColor }}
                    />

                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[8.5px] font-mono text-slate-500 font-extrabold uppercase">
                        {prod.category} &bull; {prod.subcategory}
                      </span>
                      {prod.badge && (
                        <span 
                          className="text-[8px] font-mono font-black uppercase tracking-wider py-0.5 px-2 rounded-full border border-current"
                          style={{
                            color: prod.glowColor === 'red' ? '#ef5350' : prod.glowColor === 'purple' ? '#c084fc' : prod.glowColor === 'amber' ? '#fbbf24' : '#22d3ee',
                            backgroundColor: prod.glowColor === 'red' ? '#ef535020' : prod.glowColor === 'purple' ? '#c084fc20' : prod.glowColor === 'amber' ? '#fbbf2420' : '#22d3ee20'
                          }}
                        >
                          {prod.badge}
                        </span>
                      )}
                    </div>

                    <h4 className="font-sans font-bold text-sm tracking-tight text-white uppercase group-hover:text-cyan-400 transition-colors line-clamp-1 mb-1">
                      {prod.name}
                    </h4>
                    <span className="font-mono text-[9px] text-[#00ffff] font-semibold bg-cyan-950/20 py-0.5 px-1.5 rounded">{prod.codename}</span>

                    <p className="text-[10.5px] text-slate-400 leading-normal font-sans line-clamp-2 my-3">
                      {prod.description}
                    </p>

                    {/* Faux 3D Grid Asset Representation inside the card */}
                    <div className="h-16 w-full bg-[#05070f] border border-slate-900 rounded-lg relative my-3.5 overflow-hidden flex items-center justify-center">
                      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,255,0.02)_1px,transparent_1px)] bg-[size:10px_10px] opacity-40 group-hover:scale-110 transition-transform duration-500" />
                      
                      {/* Interactive CSS 3D elements */}
                      <div className="w-10 h-10 border border-dashed rounded-full flex items-center justify-center group-hover:rotate-180 transition-transform duration-700"
                           style={{ borderColor: accentColor }}
                      >
                        <Cpu className="w-4 h-4 text-slate-500 group-hover:text-cyan-300 transition-colors" />
                      </div>
                    </div>

                    <div className="flex items-center justify-between border-t border-slate-900/80 pt-3.5 mt-2 font-mono">
                      <div>
                        <span className="text-[8px] text-slate-500 block uppercase font-bold">NEXORAX NET</span>
                        <span className="text-sm font-black text-white">
                          {formatNexoraXCurrency(prod.price * currencyRate * profileMultiplier, 0)}
                        </span>
                      </div>
                      <span className="text-[9px] font-extrabold uppercase tracking-wide text-cyan-400 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                        Secure Specifications 
                        <ChevronRight className="w-3.5 h-3.5 text-cyan-400" />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* MULTI-PAGE PORTAL NAVIGATOR PAGER (Spans all 50 directory indices) */}
          {totalPages > 1 && (
            <div className="bg-black/85 border border-slate-800 rounded-xl p-3 flex items-center justify-between font-mono text-xs relative z-10">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                className="py-1 px-3 bg-slate-900 border border-slate-800 rounded hover:border-cyan-400 text-xs text-white disabled:opacity-35 cursor-pointer uppercase font-black tracking-wider transition-all"
              >
                &lt; Prior Page
              </button>

              <div className="flex items-center gap-1.5">
                {Array.from({ length: totalPages }).map((_, idx) => {
                  const pg = idx + 1;
                  return (
                    <button
                      key={pg}
                      onClick={() => setCurrentPage(pg)}
                      className={`w-7 h-7 rounded flex items-center justify-center font-bold text-[10.5px] cursor-pointer transition-all ${
                        currentPage === pg
                          ? 'bg-cyan-500/20 text-[#00ffff] border border-[#00ffff]/40 font-black shadow-[0_0_8px_rgba(0,255,255,0.15)]'
                          : 'bg-black/20 text-slate-400 border border-slate-900 hover:border-slate-800 hover:text-white'
                      }`}
                    >
                      {pg}
                    </button>
                  );
                })}
              </div>

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                className="py-1 px-3 bg-slate-900 border border-slate-800 rounded hover:border-cyan-400 text-xs text-white disabled:opacity-35 cursor-pointer uppercase font-black tracking-wider transition-all"
              >
                Next Page &gt;
              </button>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: THE HOLOGRAPHIC 3D DETAIL PANEL (PRODUCT DECK - 5 COLS OVERVIEW) */}
        <div className="lg:col-span-5 flex flex-col gap-6">
          <AnimatePresence mode="wait">
            {detailedViewMode && selectedProduct && (
              <motion.div
                key={selectedProduct.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="bg-black/90 border-[#00ffff]/40 border-2 rounded-2xl p-5 text-left relative overflow-hidden flex flex-col justify-between"
                style={{
                  minHeight: '620px',
                  boxShadow: '0 0 35px rgba(0, 255, 255, 0.12)'
                }}
              >
                {/* Visual orbital tech ring overlay behind header */}
                <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-64 h-64 border border-dashed border-[#00ffff]/10 rounded-full animate-spin pointer-events-none z-0" />
                <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-52 h-52 border border-dotted border-purple-500/10 rounded-full animate-pulse-slow pointer-events-none z-0" />

                {/* PDP Header specs */}
                <div className="relative z-10 w-full">
                  <div className="flex items-center justify-between border-b border-slate-900 pb-3 mb-4">
                    <div className="flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-[#00ffff]" />
                      <span className="text-[9.5px] font-mono text-cyan-400 font-extrabold uppercase tracking-widest">
                        Node PDP Showcase Page
                      </span>
                    </div>
                    <span className="text-[10.5px] font-mono text-emerald-400 font-bold bg-emerald-950/20 px-2 py-0.5 rounded border border-emerald-500/20 uppercase flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                      Vault Status Verified
                    </span>
                  </div>

                  {/* ADMIN CONSOLE OVERRIDE CARD GATE */}
                  <div className="bg-[#02050b] border border-cyan-500/30 rounded-xl p-3 mb-4 flex items-center justify-between shadow-[0_0_15px_rgba(0,255,255,0.05)]">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-cyan-400 animate-pulse shrink-0" />
                      <div className="text-left">
                        <span className="text-[10px] font-mono font-bold tracking-widest text-slate-300 uppercase block leading-none">
                          Product Console Core Gate
                        </span>
                        <span className="text-[8.5px] font-mono text-cyan-300 uppercase block mt-1">
                          {user?.email || 'NEXORAX NEXORAX AUTOMATOR'}
                        </span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => setIsAdminMode(!isAdminMode)}
                      className={`px-3 py-1.5 border rounded-lg font-mono text-[8.5px] uppercase font-bold tracking-widest transition-all cursor-pointer ${
                        isAdminMode
                          ? 'bg-[#00ff22]/20 border-[#00ff22] text-[#00ff22]'
                          : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-400'
                      }`}
                    >
                      {isAdminMode ? '[ADMIN PORTAL ACTIVE]' : '[ENTER ADMIN EDITOR]'}
                    </button>
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[9px] bg-purple-950/50 border border-purple-500/40 text-purple-300 py-0.5 px-2 rounded-full font-mono uppercase font-black">
                      {activeProduct.subcategory}
                    </span>
                    <span className="text-[9px] text-slate-500 font-mono font-bold">
                      SKU CODE: {activeProduct.codename}
                    </span>
                  </div>

                  <h3 className="text-xl md:text-2xl font-black font-sans tracking-tight text-white uppercase leading-tight">
                    {activeProduct.name}
                  </h3>

                  {/* Star rating summaries */}
                  <div className="flex items-center gap-1.5 mt-1">
                    <div className="flex gap-0.5 text-amber-400">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-current" />
                      ))}
                    </div>
                    <span className="text-[10px] font-mono text-slate-400">
                      {(activeProduct.stars || 5).toFixed(1)} &bull; ({(activeProduct.reviews || 88)} audited audits)
                    </span>
                  </div>

                  <p className="text-[11px] text-slate-300 leading-relaxed font-sans my-4 border-l-2 border-cyan-500/40 pl-3 italic bg-gradient-to-r from-slate-900/40 to-transparent py-1">
                    {activeProduct.description}
                  </p>

                  {/* 3D FLOATING ROTATING WIREFRAME GRID SPECIFICATION VIEWER */}
                  <div 
                    className="w-full h-44 my-4 select-none" 
                    style={{ perspective: '1000px' }}
                  >
                    <div 
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleMouseUp}
                      className="NexoraX-product-page-renderer w-full h-full bg-[#030612]/95 border-2 border-slate-900 rounded-xl flex items-center justify-center relative cursor-grab active:cursor-grabbing transition-colors duration-300 hover:border-cyan-400/50"
                      style={{
                        transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
                        transformStyle: 'preserve-3d',
                        transition: isDragging ? 'none' : 'transform 0.5s cubic-bezier(0.25, 1, 0.5, 1)',
                        backfaceVisibility: 'hidden'
                      }}
                    >
                      <div className="absolute inset-0 bg-[#00ffff]/[0.015] bg-[linear-gradient(rgba(0,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,255,0.03)_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none rounded-xl" style={{ transform: 'translateZ(5px)' }} />
                      
                      {/* Dynamic light reflection overlay simulating light hitting the 3D-rotated surface */}
                      <div 
                        className="absolute inset-0 pointer-events-none rounded-xl z-30 transition-opacity duration-300"
                        style={{
                          background: `linear-gradient(${135 - rotation.y + rotation.x * 0.5}deg, rgba(255, 255, 255, ${0.15 + Math.abs(rotation.y) * 0.001}) 0%, rgba(0, 255, 255, ${0.05 + Math.abs(rotation.x) * 0.001}) 35%, rgba(0, 0, 0, 0.3) 80%, rgba(0, 0, 0, 0.7) 100%)`,
                          mixBlendMode: 'overlay',
                          transform: 'translateZ(30px)',
                          opacity: 0.85
                        }}
                      />
                      
                      {/* Generates our fully editable 3D custom geometry rendering core */}
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center" style={{ transform: 'translateZ(45px) translate(-50%, -50%)', transformStyle: 'preserve-3d font-sans' }}>
                        {renderInteractive3DGeometry(activeProduct)}
                        
                        <div className="absolute font-mono text-[8px] text-center uppercase tracking-widest text-slate-500 mt-28 whitespace-nowrap">
                          INTERACTIVE DIAL HARVESTOR MODEL (DRAG TO ROTATE)
                        </div>
                      </div>

                      <div className="absolute bottom-2 left-3 flex gap-2 font-mono text-[8.5px]" style={{ transform: 'translateZ(25px)' }}>
                        <span className="text-slate-500 uppercase">LAT: <strong className="text-cyan-400">0.8s SAFE</strong></span>
                        <span className="text-slate-500 uppercase">LEVY: <strong className="text-[#00ff22]">1.0% LOCK</strong></span>
                      </div>

                      {/* Faux Size selectors indicating custom selections */}
                      <div className="absolute right-3.5 bottom-3.5 flex flex-col gap-1 text-right" style={{ transform: 'translateZ(25px)' }}>
                        <span className="text-[8px] text-slate-500 uppercase font-black tracking-widest">Active Selector Range</span>
                        <div className="flex gap-1 animate-pulse">
                          {['S', 'M', 'L', 'XL', 'NODE'].map((sz) => (
                            <span key={sz} className={`text-[8px] font-mono py-0.5 px-1.5 border rounded-sm font-extrabold ${sz === 'NODE' ? 'bg-[#e840f3] text-white border-white/20' : 'bg-black/60 border-slate-800 text-slate-400'}`}>
                              {sz}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {isAdminMode ? (
                    /* ADMIN INLINE EDITING INTERFACE COMPONENT */
                    <div className="space-y-4 my-2 text-xs font-mono relative z-10 p-4 bg-slate-950/60 border border-dashed border-[#00ffff]/20 rounded-xl max-h-[580px] overflow-y-auto custom-scrollbar">
                      <div className="flex items-center gap-1.5 border-b border-slate-900 pb-2 mb-2">
                        <Sliders className="w-4 h-4 text-cyan-400 shrink-0" />
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#00ffff] font-black">
                          Secure Registry Inline Mod Core
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-[8px] text-slate-500 uppercase block">Product Name</label>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="w-full bg-[#030612] border border-slate-850 p-2 rounded text-[10px] text-white outline-none focus:border-[#00ffff] font-sans font-bold"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[8px] text-slate-500 uppercase block">Codename SKU</label>
                          <input
                            type="text"
                            value={editCodename}
                            onChange={(e) => setEditCodename(e.target.value)}
                            className="w-full bg-[#030612] border border-slate-850 p-2 rounded text-[10px] text-cyan-300 outline-none focus:border-[#00ffff] font-mono font-bold"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1">
                          <label className="text-[8px] text-slate-500 uppercase block">Category</label>
                          <select
                            value={editCategory}
                            onChange={(e) => setEditCategory(e.target.value as any)}
                            className="w-full bg-[#030612] border border-slate-850 p-2 rounded text-[10px] text-white outline-none focus:border-[#00ffff]"
                          >
                            {['Infrastructure', 'Communications', 'Neural', 'Workspace', 'Security'].map((cat) => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="text-[8px] text-slate-500 uppercase block">Subcategory</label>
                          <input
                            type="text"
                            value={editSubcategory}
                            onChange={(e) => setEditSubcategory(e.target.value)}
                            className="w-full bg-[#030612] border border-slate-850 p-2 rounded text-[10px] text-white outline-none"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[8px] text-slate-500 uppercase block">Badge Label</label>
                          <input
                            type="text"
                            value={editBadge}
                            onChange={(e) => setEditBadge(e.target.value)}
                            placeholder="e.g. ULTRA NODE"
                            className="w-full bg-[#030612] border border-slate-850 p-2 rounded text-[10px] text-white outline-none"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div className="space-y-1 md:col-span-2">
                          <label className="text-[8px] text-slate-500 uppercase block">Base Price (GBP £)</label>
                          <input
                            type="number"
                            value={editPrice}
                            onChange={(e) => setEditPrice(Number(e.target.value))}
                            className="w-full bg-[#030612] border border-slate-850 p-2 rounded text-[10px] text-emerald-400 font-bold outline-none"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[8px] text-slate-500 uppercase block">Neon Color</label>
                          <select
                            value={editGlowColor}
                            onChange={(e) => setEditGlowColor(e.target.value)}
                            className="w-full bg-[#030612] border border-slate-850 p-2 rounded text-[10px] text-white outline-none"
                          >
                            {['cyan', 'purple', 'lime', 'rose', 'amber', 'indigo', 'emerald'].map((col) => (
                              <option key={col} value={col}>{col.toUpperCase()}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[8px] text-slate-500 uppercase block">Specification Description</label>
                        <textarea
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          rows={2}
                          className="w-full bg-[#030612] border border-slate-850 p-2 rounded text-[10px] text-slate-300 outline-none font-sans resize-none"
                        />
                      </div>

                      {/* 3D configurations */}
                      <div className="border border-purple-500/20 bg-purple-950/10 rounded-lg p-3 space-y-2">
                        <span className="text-[8.5px] font-mono uppercase tracking-wider text-purple-300 font-extrabold block">
                          3D Interactive Visualizer Settings
                        </span>
                        
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <label className="text-[8px] text-slate-500 uppercase block">Geometry Wireframe</label>
                            <select
                              value={editGeometryType}
                              onChange={(e) => setEditGeometryType(e.target.value as any)}
                              className="w-full bg-[#030612] border border-slate-800 p-1.5 rounded text-[10px]"
                            >
                              <option value="circle">Concentric Tech Rings</option>
                              <option value="cube">Holographic Cuboid</option>
                              <option value="prism">Quantum Tetrahedron</option>
                              <option value="torus">Cosmic Orbiters</option>
                              <option value="sphere">Dotted Shell Shell</option>
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-[8px] text-slate-500 uppercase block">Breath Rhythm</label>
                            <select
                              value={editPulseRate}
                              onChange={(e) => setEditPulseRate(e.target.value as any)}
                              className="w-full bg-[#030612] border border-slate-800 p-1.5 rounded text-[10px]"
                            >
                              <option value="none">Static Balance</option>
                              <option value="slow">Gentle Breathe</option>
                              <option value="fast">Emergency Throttle</option>
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 pt-2">
                          <div className="space-y-1">
                            <div className="flex justify-between items-center text-[8px]">
                              <span className="text-slate-500 uppercase">Glow Radius</span>
                              <span className="text-purple-400 font-bold">{editGlowIntensity}px</span>
                            </div>
                            <input
                              type="range"
                              min="4"
                              max="40"
                              value={editGlowIntensity}
                              onChange={(e) => setEditGlowIntensity(Number(e.target.value))}
                              className="w-full accent-purple-400 h-1 bg-slate-900 rounded"
                            />
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between items-center text-[8px]">
                              <span className="text-slate-500 uppercase">Line Weight</span>
                              <span className="text-purple-400 font-bold">{editStrokeWidth}px</span>
                            </div>
                            <input
                              type="range"
                              min="0.5"
                              max="5"
                              step="0.5"
                              value={editStrokeWidth}
                              onChange={(e) => setEditStrokeWidth(Number(e.target.value))}
                              className="w-full accent-purple-400 h-1 bg-slate-900 rounded"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Machine parameter specs */}
                      <div className="border border-slate-900 p-2.5 rounded-lg bg-black/40 space-y-2">
                        <span className="text-[8px] text-slate-500 uppercase block font-extrabold">Audited System Specs Fields</span>
                        <div className="grid grid-cols-1 gap-2">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={editSpecKey1}
                              onChange={(e) => setEditSpecKey1(e.target.value)}
                              className="w-1/3 bg-[#030612] border border-slate-800 p-1 rounded font-bold text-slate-500 uppercase text-[9px]"
                            />
                            <input
                              type="text"
                              value={editSpecVal1}
                              onChange={(e) => setEditSpecVal1(e.target.value)}
                              className="flex-1 bg-[#030612] border border-slate-800 p-1 rounded font-bold text-white text-[9px]"
                            />
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={editSpecKey2}
                              onChange={(e) => setEditSpecKey2(e.target.value)}
                              className="w-1/3 bg-[#030612] border border-slate-800 p-1 rounded font-bold text-slate-500 uppercase text-[9px]"
                            />
                            <input
                              type="text"
                              value={editSpecVal2}
                              onChange={(e) => setEditSpecVal2(e.target.value)}
                              className="flex-1 bg-[#030612] border border-slate-800 p-1 rounded font-bold text-white text-[9px]"
                            />
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={editSpecKey3}
                              onChange={(e) => setEditSpecKey3(e.target.value)}
                              className="w-1/3 bg-[#030612] border border-slate-800 p-1 rounded font-bold text-slate-500 uppercase text-[9px]"
                            />
                            <input
                              type="text"
                              value={editSpecVal3}
                              onChange={(e) => setEditSpecVal3(e.target.value)}
                              className="flex-1 bg-[#030612] border border-slate-800 p-1 rounded font-bold text-white text-[9px]"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Feature Checklist */}
                      <div className="space-y-2 border border-slate-900 p-2.5 rounded-lg bg-black/40">
                        <label className="text-[8px] text-slate-500 uppercase block font-extrabold">Verification Checklist</label>
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={newFeatureText}
                            onChange={(e) => setNewFeatureText(e.target.value)}
                            placeholder="Add bullet segment"
                            className="flex-1 bg-[#030612] border border-slate-800 p-1 rounded text-[10px]"
                          />
                          <button
                            onClick={() => {
                              if (newFeatureText.trim()) {
                                setEditFeatures([...editFeatures, newFeatureText.trim()]);
                                setNewFeatureText('');
                              }
                            }}
                            className="px-2 py-1 bg-cyan-950/40 border border-[#00ffff]/20 text-cyan-400 rounded text-[9px] hover:border-cyan-400"
                          >
                            Add
                          </button>
                        </div>
                        <div className="max-h-24 overflow-y-auto custom-scrollbar space-y-1">
                          {editFeatures.map((feat, i) => (
                            <div key={i} className="flex justify-between items-center text-[9px] bg-[#030612] p-1.5 rounded">
                              <span className="truncate pr-1 text-slate-300">{feat}</span>
                              <button
                                onClick={() => setEditFeatures(editFeatures.filter((_, idx) => idx !== i))}
                                className="text-red-400 hover:text-red-300"
                              >
                                remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Save Database Call Button */}
                      <button
                        onClick={handleSaveProductDatabase}
                        disabled={isSavingProduct}
                        className="w-full py-2.5 bg-gradient-to-r from-emerald-950 to-emerald-900 border border-emerald-500/30 hover:border-emerald-400 text-white rounded-lg font-bold font-mono tracking-widest text-[9.5px] uppercase flex items-center justify-center gap-2 cursor-pointer transition-all hover:shadow-[0_0_15px_rgba(16,185,129,0.15)]"
                      >
                        {isSavingProduct ? (
                          <>
                            <RefreshCw className="w-3.5 h-3.5 text-emerald-400 animate-spin" />
                            COMMITTING MOUNTED LEDGER NODE...
                          </>
                        ) : (
                          <>
                            <Database className="w-3.5 h-3.5 text-emerald-400" />
                            SAVE ASSET CHANGES TO STATE & FIRESTORE
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* SPECIFICATION SHEET */}
                      <div className="space-y-2 mb-4">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#00ffff]/70 font-black block">
                          Audited Machine Parameters
                        </span>
                        <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                          {Object.entries(activeProduct.specs || {}).map(([lbl, val]) => (
                            <div key={lbl} className="bg-slate-900/30 border border-slate-900 p-2 rounded-lg">
                              <span className="text-[8px] text-slate-500 uppercase block">{lbl}</span>
                              <span className="text-white font-bold leading-none text-[10px] break-all">{val}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* PRODUCT FEATURES BULLETIN */}
                      <div className="bg-slate-950/80 border border-slate-900 rounded-xl p-3 mb-4 space-y-1.5">
                        <span className="text-[10px] font-mono uppercase text-slate-400 font-extrabold tracking-wider block">
                          Feature Verification Handshakes:
                        </span>
                        <ul className="space-y-1 font-mono text-[9px] text-slate-300">
                          {(activeProduct.features || []).map((feat, idx) => (
                            <li key={idx} className="flex items-start gap-1 pb-1 border-b border-slate-900 last:border-0">
                              <span className="text-[#00ffff] font-black mr-1 shrink-0">&raquo;</span>
                              <span className="leading-relaxed text-left">{feat}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* INTERACTIVE COMPONENT CONFIGURATOR PANEL */}
                      <div className="bg-black/60 border border-purple-500/20 rounded-xl p-4 mb-4 space-y-3.5 text-left">
                        <div className="flex items-center gap-1">
                          <Sliders className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                          <span className="text-[9.5px] font-mono text-purple-300 font-black uppercase tracking-wider">
                            Custom Dial Calibration Core
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-xs font-mono">
                          {/* Cooling parameter */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <label className="text-[8.5px] text-slate-500 uppercase block">Water Recirc Velocity</label>
                              <span className="text-[#00ffff] text-[9.5px] font-bold">{customCoolingSpeed} GPM</span>
                            </div>
                            <input
                              type="range"
                              min="10"
                              max="200"
                              value={customCoolingSpeed}
                              onChange={(e) => setCustomCoolingSpeed(Number(e.target.value))}
                              className="w-full h-1 bg-slate-900 rounded-lg appearance-none cursor-pointer accent-[#00ffff]"
                            />
                          </div>

                          {/* Haptic strength parameter */}
                          <div className="space-y-1">
                            <label className="text-[8.5px] text-slate-500 uppercase block">Analog Tension</label>
                            <select
                              value={customHanticTension}
                              onChange={(e) => setCustomHanticTension(e.target.value)}
                              className="w-full bg-[#05060b] border border-slate-800 p-1 rounded text-[9.5px] text-white outline-none focus:border-[#00ffff]"
                            >
                              <option value="Precision Lock">Precision Lock (Tight)</option>
                              <option value="Medium-Tight">Adjustable Smooth</option>
                              <option value="Zero Resistance">Zero-friction Hover</option>
                            </select>
                          </div>

                          {/* Laser tracking Hertz */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <label className="text-[8.5px] text-slate-500 uppercase block">Tracking Frequency</label>
                              <span className="text-[#e840f3] text-[9.5px] font-bold">{customFrequencyHz} Hz</span>
                            </div>
                            <input
                              type="range"
                              min="60"
                              max="720"
                              step="4"
                              value={customFrequencyHz}
                              onChange={(e) => setCustomFrequencyHz(Number(e.target.value))}
                              className="w-full h-1 bg-slate-900 rounded-lg appearance-none cursor-pointer accent-[#e840f3]"
                            />
                          </div>

                          {/* Hardware tagging ID */}
                          <div className="space-y-1">
                            <label className="text-[8.5px] text-slate-500 uppercase block">System Mirroring Identifier</label>
                            <input
                              type="text"
                              value={customTelemetryTag}
                              onChange={(e) => setCustomTelemetryTag(e.target.value)}
                              className="w-full bg-[#05060b] border border-slate-800 p-1 rounded text-[9.5px] text-[#00ff22] font-mono outline-none"
                            />
                          </div>
                        </div>

                        <button
                          onClick={handleSaveSpecifications}
                          disabled={isSyncingWithGDrive}
                          className="w-full py-2 bg-gradient-to-r from-[#030e20] to-[#120422] hover:bg-slate-900 border border-slate-800 hover:border-cyan-400 text-slate-300 hover:text-white rounded-lg font-mono text-[9px] uppercase font-bold tracking-wider transition-all cursor-pointer flex items-center justify-center gap-2"
                        >
                          <Database className={`w-3 h-3 text-cyan-400 ${isSyncingWithGDrive ? 'animate-spin' : ''}`} />
                          {isSyncingWithGDrive ? 'COMPILING IMPERIAL REPLICATOR...' : 'Secure & Mirror Specifications'}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* THE MANDATORY MULTI-STAGE EXTRACT LEVY LEDGER GRID */}
                <div className="relative z-10 border-t border-slate-900 pt-4.5 mt-2 bg-black/60 p-4 rounded-xl border border-dashed border-[#00ffff]/20">
                  <div className="flex items-center gap-1.5 border-b border-slate-900 pb-2 mb-2">
                    <Landmark className="w-4 h-4 text-[#00ff22]" />
                    <span className="text-[9.5px] font-mono uppercase tracking-widest text-[#00ff22] font-black">
                      NexoraX Ledger Flow math (Node-07 Compliance)
                    </span>
                  </div>

                  <div className="font-mono text-[10.5px] space-y-1.5 text-slate-400">
                    <div className="flex justify-between">
                      <span>1. Gross Settle Cost:</span>
                      <span className="text-white font-extrabold">
                        {formatNexoraXCurrency(finalAdjustedPrice, 2)}
                      </span>
                    </div>

                    {/* Levy splitting detail drop */}
                    <div className="bg-[#050a12] border border-slate-900 p-2 rounded text-[9px] space-y-1 block text-left">
                      <div className="flex justify-between text-[#00ff22] font-bold">
                        <span>2. 1% Perpetual 3% Community Allocation:</span>
                        <span>-{formatNexoraXCurrency(levyTotal, 2)}</span>
                      </div>
                      <div className="pl-2 border-l border-emerald-900 text-slate-500 space-y-0.5">
                        <div className="flex justify-between text-[8px]">
                          <span>&bull; Charity 1 (Dunfermline Alignment):</span>
                          <span>{formatNexoraXCurrency(charity1Split, 2)}</span>
                        </div>
                        <div className="flex justify-between text-[8px]">
                          <span>&bull; Charity 2 (Global Distribution):</span>
                          <span>{formatNexoraXCurrency(charity2Split, 2)}</span>
                        </div>
                        <div className="flex justify-between text-[8px]">
                          <span>&bull; Charity 3 (United Kingdom Core):</span>
                          <span>{formatNexoraXCurrency(charity3Split, 2)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between text-[#e840f3] font-bold">
                      <span>3. 10% Maintenance & Hosting Fee (Admin Fee):</span>
                      <span>-{formatNexoraXCurrency(lifetimeGavelTax, 2)}</span>
                    </div>

                    <div className="flex justify-between text-white font-black border-t border-slate-900 pt-2 text-[11.5px]">
                      <span className="text-[#00ffff] tracking-widest">4. NET SETTLEMENT (SecurePayment Routing):</span>
                      <span className="text-[#00ffff] animate-pulse">
                        {formatNexoraXCurrency(netSettlementToSecurePayment, 2)}
                      </span>
                    </div>
                  </div>

                  {/* EXECUTE ACTION TRIGGER & PAY BUTTON */}
                  <div className="mt-4 pt-1 flex gap-2 w-full">
                    <button
                      onClick={() => {
                        setCartCount(prev => prev + 1);
                        triggerNotification(`NEXORAX HANDSHAKE: Incremented shopping ledger cache by 1 node. Current: ${cartCount + 1}`);
                      }}
                      className="py-3 px-4 bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-[#00ffff] text-white font-mono text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 shrink-0"
                    >
                      Add To System Cache
                    </button>
                    
                    <a 
                      href="https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00"
                      target="_blank"
                      rel="noreferrer"
                      className="flex-1 text-center py-3 bg-[#00ff22] hover:bg-[#00dd1d] text-black font-black uppercase tracking-wider text-xs rounded-xl hover:shadow-[0_0_15px_rgba(0,255,34,0.3)] transition-all cursor-pointer border border-[#00ff22]/40"
                    >
                      Acquire Node Invoice Link &raquo;
                    </a>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* FOOTER METRIC BRANDING */}
      <footer className="relative z-10 w-full mt-auto bg-black/85 border-t border-slate-900 py-8 px-6 text-slate-500 font-mono text-xs text-center border-t border-cyan-500/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-left space-y-1">
            <div className="text-white font-extrabold uppercase tracking-widest text-[11px] flex items-center gap-1">
              <Cpu className="w-3.5 h-3.5 text-[#00ffff]" />
              NEXORAX DIGITAL LTD [SC887095]
            </div>
            <p className="text-[10px] text-slate-400 font-sans max-w-lg">
              Authorized representative under direct command from the Office of the Director, Lead Director. All transactions compiled synchronously using Tier-One encryption protocols.
            </p>
          </div>

          <div className="text-right space-y-1">
            <div className="text-[9.5px] uppercase tracking-wide">
              Compliance status: <span className="text-[#00ff22] font-black">ACTIVE UK GRANTS</span>
            </div>
            <div className="text-[9px] text-slate-500">
              Intellectual Property patents pending &bull; &copy; 2026 NexoraX Digital Ltd. All NexoraXty indices locked.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
